<template>
  <div>
    <!-- Consents List Card -->
    <b-card class="modern-card">
      <div class="gradient-line-wrapper"></div>

      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h4 class="mb-1 card-title-modern">{{ $t("Consent Forms") }}</h4>
          <p class="text-muted mb-0 small">
            {{ $t("Manage and review student consent forms") }}
          </p>
        </div>
      </div>

      <!-- Clean Professional Summary Cards -->
      <b-row class="mb-3" v-if="!summaryLoading">
        <b-col xl="3" lg="3" md="6" sm="12" class="mb-2">
          <div class="professional-stat-card border-primary">
            <div class="stat-body">
              <div class="stat-info">
                <span class="stat-label">{{ $t("TOTAL FORMS") }}</span>
                <h2 class="stat-value text-primary">{{ totalCount }}</h2>
              </div>
              <div class="stat-icon bg-light-primary">
                <feather-icon icon="FileTextIcon" size="24" />
              </div>
            </div>
            <div class="stat-footer bg-light-primary text-primary">
              {{ $t("Consents in library") }}
            </div>
          </div>
        </b-col>
        <b-col xl="3" lg="3" md="6" sm="12" class="mb-2">
          <div class="professional-stat-card border-success">
            <div class="stat-body">
              <div class="stat-info">
                <span class="stat-label">{{ $t("TOTAL CONSENT") }}</span>
                <h2 class="stat-value text-success">{{ summaryData.total }}</h2>
              </div>
              <div class="stat-icon bg-light-success">
                <feather-icon icon="UserCheckIcon" size="24" />
              </div>
            </div>
            <div class="stat-footer bg-light-success text-success">
              {{ $t("Responses received") }}
            </div>
          </div>
        </b-col>
        <b-col xl="3" lg="3" md="6" sm="12" class="mb-2">
          <div class="professional-stat-card border-warning">
            <div class="stat-body">
              <div class="stat-info">
                <span class="stat-label">{{ $t("APPROVED") }}</span>
                <h2 class="stat-value text-warning">
                  {{ summaryData.approved }}
                </h2>
              </div>
              <div class="stat-icon bg-light-warning">
                <feather-icon icon="CheckIcon" size="24" />
              </div>
            </div>
            <div class="stat-footer bg-light-warning text-warning">
              {{ $t("Approved by parents") }}
            </div>
          </div>
        </b-col>
        <b-col xl="3" lg="3" md="6" sm="12" class="mb-2">
          <div class="professional-stat-card border-danger">
            <div class="stat-body">
              <div class="stat-info">
                <span class="stat-label">{{ $t("REJECTED") }}</span>
                <h2 class="stat-value text-danger">
                  {{ summaryData.rejected }}
                </h2>
              </div>
              <div class="stat-icon bg-light-danger">
                <feather-icon icon="XIcon" size="24" />
              </div>
            </div>
            <div class="stat-footer bg-light-danger text-danger">
              {{ $t("Disapproved forms") }}
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Summary Loading Skeleton -->
      <b-row class="mb-3" v-else>
        <b-col
          v-for="n in 4"
          :key="'summary-loader-' + n"
          xl="3"
          lg="3"
          md="6"
          sm="12"
          class="mb-2"
        >
          <div class="professional-stat-card skeleton">
            <div class="skeleton-shimmer"></div>
          </div>
        </b-col>
      </b-row>

      <b-row class="align-items-center filter-row-single-line mb-2">
        <!-- Search Input -->
        <b-col xl="3" lg="3" md="12" sm="12" cols="12" class="mb-1 mb-lg-0">
          <div style="position: relative">
            <b-form-input
              v-model="searchQuery"
              :placeholder="$t('Search by student, title') + '...'"
              class="modern-input"
              style="padding-right: 2.5rem !important"
              @input="onSearchChange"
            />
            <feather-icon
              icon="SearchIcon"
              size="16"
              style="
                position: absolute;
                right: 12px;
                top: 50%;
                transform: translateY(-50%);
                color: #9ca3af;
              "
            />
          </div>
        </b-col>

        <!-- Class Filter -->
        <b-col xl="2" lg="2" md="6" sm="12" cols="12" class="mb-1 mb-lg-0">
          <v-select
            v-model="filterClassID"
            :options="classesData"
            label="name"
            :reduce="(val) => val.id"
            :placeholder="$t('All ' + getLabel('Classes'))"
            class="custom-filter-select"
            append-to-body
            @input="onSearchChange"
          />
        </b-col>

        <!-- Status Filter -->
        <b-col xl="2" lg="2" md="6" sm="12" cols="12" class="mb-1 mb-lg-0">
          <v-select
            v-model="filterStatus"
            :options="statusOptions"
            label="label"
            :reduce="(val) => val.value"
            :placeholder="$t('All Status')"
            class="custom-filter-select"
            append-to-body
            @input="onSearchChange"
          />
        </b-col>

        <!-- Date Range Filter -->
        <b-col xl="2" lg="2" md="6" sm="12" cols="12" class="mb-1 mb-lg-0">
          <flat-pickr
            v-model="filterDateRange"
            :config="filterDateConfig"
            :placeholder="$t('Date Range')"
            class="form-control modern-input"
            @on-change="onSearchChange"
          />
        </b-col>

        <!-- Action Buttons -->
        <b-col
          xl="3"
          lg="3"
          md="12"
          sm="12"
          cols="12"
          class="text-right d-flex align-items-center justify-content-end"
          style="gap: 10px"
        >
          <!-- Export Button -->
          <b-button
            v-if="rights.view"
            variant="outline-primary"
            class="action-btn-header"
            @click="onExport"
            :disabled="dataLoading"
            v-b-tooltip.hover.top="$t('Export to Excel')"
          >
            <feather-icon icon="FileTextIcon" size="18" />
          </b-button>

          <!-- Print Button -->
          <b-button
            v-if="rights.view"
            variant="outline-primary"
            class="action-btn-header"
            @click="onPrint"
            :disabled="dataLoading"
            v-b-tooltip.hover.top="$t('Print Report')"
          >
            <feather-icon icon="PrinterIcon" size="18" />
          </b-button>

          <!-- Add Button -->
          <b-button
            v-if="rights.add"
            @click="openAddModal"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="gradient-primary"
            class="action-btn-filter"
            style="
              min-width: 120px;
              justify-content: center;
              height: 44px;
              border: 1px solid transparent !important;
              border-radius: 10px;
            "
            :disabled="saving"
          >
            <b-spinner v-if="saving" small class="mr-1" />
            <feather-icon v-else icon="PlusIcon" size="16" class="mr-50" />
            <span class="font-weight-bold">{{ $t("Add") }}</span>
          </b-button>
        </b-col>
      </b-row>

      <!-- Pagination Controls - Top (Compact Single Line) -->
      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-2"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <!-- Page Size Dropdown -->
          <div class="page-size-wrapper">
            <label class="page-size-label">{{ $t("Show") }}:</label>
            <v-select
              :key="'page-size-' + perPage"
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
              @option:selected="closePageSizeDropdown"
              ref="pageSizeSelect"
            />
          </div>
        </div>

        <!-- Count Display in Middle -->
        <div class="pagination-count-display">
          <span class="count-text">
            {{ $t("Showing") }} <strong>{{ displayedRange.start }}</strong>
            {{ $t("to") }} <strong>{{ displayedRange.end }}</strong>
            {{ $t("of") }} <strong>{{ filteredTotalCount }}</strong>
          </span>
        </div>

        <!-- Pagination -->
        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact"
            @change="onPageChange"
          />
        </div>
      </div>

      <!-- Top Scrollbar for PC -->
      <div class="table-scrollbar-top" ref="topScrollbar">
        <div class="table-scrollbar-content"></div>
      </div>

      <div class="table-wrapper" ref="tableWrapper" @scroll="syncScroll">
        <b-table
          class="modern-table"
          :items="paginatedConsents"
          :fields="fields"
          :busy="dataLoading"
          hover
          show-empty
          :empty-text="$t('No consent forms found')"
        >
          <template #table-busy>
            <div class="text-center py-4">
              <b-spinner variant="primary" />
            </div>
          </template>

          <template #head()="data">
            <span class="text-uppercase font-weight-bolder small">{{
              $t(data.label)
            }}</span>
          </template>

          <template #cell(sno)="{ index }">
            <span class="text-dark font-weight-bold">{{
              (currentPage - 1) * perPage + index + 1
            }}</span>
          </template>

          <template #cell(title)="{ item }">
            <div class="font-weight-bold text-dark">
              {{ item.title || "N/A" }}
            </div>
          </template>

          <template #cell(description)="{ item }">
            <div :title="item.description">
              {{
                item.description
                  ? item.description.substring(0, 60) +
                    (item.description.length > 60 ? "..." : "")
                  : "-"
              }}
            </div>
          </template>

          <template #cell(class)="{ item }">
            <b-badge variant="light-primary" class="font-weight-bold">
              {{ item.className || "N/A" }}
            </b-badge>
          </template>

          <template #cell(metaCampusName)="{ item }">
            <span class="text-dark font-weight-bold">{{
              item.metaCampusName || "N/A"
            }}</span>
          </template>

          <template #cell(date)="{ item }">
            <span class="text-dark font-weight-bold">{{
              formatDate(item.date)
            }}</span>
          </template>

          <template #cell(response_stats)="{ item }">
            <div class="d-flex flex-column">
              <div class="small mb-25">
                <span class="text-primary"
                  >Total: <strong>{{ item.totalCount }}</strong></span
                >
              </div>
              <div class="d-flex gap-1" style="gap: 8px">
                <b-badge
                  variant="light-success"
                  v-b-tooltip.hover
                  :title="$t('Approved')"
                >
                  <feather-icon icon="CheckIcon" size="12" />
                  {{ item.acceptedCount }}
                </b-badge>
                <b-badge
                  variant="light-warning"
                  v-b-tooltip.hover
                  :title="$t('Pending')"
                >
                  <feather-icon icon="ClockIcon" size="12" />
                  {{ item.pendingCount }}
                </b-badge>
                <b-badge
                  variant="light-danger"
                  v-b-tooltip.hover
                  :title="$t('Rejected')"
                >
                  <feather-icon icon="XIcon" size="12" />
                  {{ item.rejectedCount }}
                </b-badge>
              </div>
            </div>
          </template>

          <template #cell(actions)="{ item }">
            <div class="d-flex align-items-center justify-content-center">
              <!-- View Button -->
              <b-button
                variant="gradient-primary"
                class="btn-icon action-btn-view mr-1"
                v-b-tooltip.hover
                :title="$t('View Student Responses')"
                @click="viewConsent(item, true)"
              >
                <feather-icon icon="UsersIcon" size="16" />
              </b-button>

              <!-- Individual Print Button -->
              <b-button
                variant="outline-primary"
                class="btn-icon action-btn-outline-primary mr-1"
                v-b-tooltip.hover
                :title="$t('Print Summary')"
                @click.stop="onPrintSingle(item)"
              >
                <feather-icon icon="PrinterIcon" size="16" />
              </b-button>

              <!-- Edit Button -->
              <b-button
                v-if="rights.edit"
                variant="outline-success"
                class="btn-icon action-btn-outline-success mr-1"
                v-b-tooltip.hover
                :title="$t('Edit')"
                @click="editConsent(item)"
              >
                <feather-icon icon="EditIcon" size="16" />
              </b-button>

              <!-- Delete Button -->
              <b-button
                v-if="rights.delete"
                variant="outline-danger"
                class="btn-icon action-btn-outline-danger"
                v-b-tooltip.hover
                :title="$t('Delete')"
                @click="deleteConsent(item)"
              >
                <feather-icon icon="Trash2Icon" size="16" />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>

    <!-- Add/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="isEdit ? $t('Edit Consent Form') : $t('Add Consent Form')"
      @hidden="resetForm"
      centered
      size="lg"
      no-close-on-backdrop
      modal-class="modern-modal"
    >
      <div class="modal-body-content p-1 pb-3">
        <!-- Row 1: Consent Title -->
        <b-row class="mb-1">
          <b-col md="12">
            <b-form-group :label="$t('Consent Title')" label-for="title">
              <b-form-input
                id="title"
                v-model="form.consent.title"
                :placeholder="$t('e.g. Annual Trip')"
                class="modern-input-field"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Row 2: Campus (if Head Office) -->
        <b-row class="mb-1" v-if="isHeadOffice">
          <b-col md="12">
            <b-form-group :label="$t('Campus')" label-for="modal-campus">
              <v-select
                id="modal-campus"
                v-model="form.campusID"
                :options="campuses"
                :clearable="false"
                label="branch"
                :reduce="(val) => val.id"
                :placeholder="$t('Select Campus')"
                class="modern-select-field"
                multiple
                @input="fetchClassesForModal"
                :disabled="isEdit"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Row 3: Class and Section -->
        <b-row class="mb-1">
          <b-col md="6">
            <b-form-group
              :label="$t('Select ' + getLabel('Class'))"
              label-for="class"
            >
              <b-skeleton
                v-if="modalLoadingClasses"
                width="100%"
                height="40px"
              />
              <v-select
                v-else
                id="class"
                v-model="selectedClasses"
                multiple
                :closeOnSelect="false"
                :options="[
                  { id: 0, name: 'All ' + getLabel('Classes') },
                  ...classesData,
                ]"
                :reduce="(val) => val.id"
                label="name"
                :placeholder="$t('Select ' + getLabel('Class'))"
                @input="onClassChange"
                class="modern-select-field"
                :disabled="
                  !form.campusID || form.campusID.length === 0 || isEdit
                "
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              :label="$t('Select ' + getLabel('Section'))"
              label-for="sections"
            >
              <b-skeleton
                v-if="modalLoadingClasses"
                width="100%"
                height="40px"
              />
              <v-select
                v-else
                id="sections"
                v-model="form.sections"
                :options="[
                  { id: 0, section: 'All ' + getLabel('Sections') },
                  ...sectionsOptions,
                ]"
                :reduce="(val) => val.id"
                label="section"
                multiple
                :closeOnSelect="false"
                @input="onSectionChange"
                :placeholder="$t('Select ' + getLabel('Sections'))"
                :disabled="selectedClasses.length == 0 || isEdit"
                class="modern-select-field"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            v-if="['myskoolhappinessfoundation'].includes(reportDomain)"
          >
            <b-form-group
              :label="$t('Age Category')"
              :invalid-feedback="$t('Age category is required.')"
            >
              <v-select
                v-model="form.house"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="ageOptions"
                :clearable="false"
                label="text"
                :reduce="(val) => val.value"
                :disabled="isEdit"
                :placeholder="$t('Select age category')"
                class="modern-select-field"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group
          :label="$t('Instructions / Description')"
          label-for="description"
        >
          <b-form-textarea
            id="description"
            v-model="form.consent.description"
            :placeholder="
              $t('Provide details about the consent requirements') + '...'
            "
            rows="4"
            class="modern-textarea-field"
          />
        </b-form-group>
      </div>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          @click="showModal = false"
          :disabled="saving"
          class="btn-cancel-modern"
        >
          {{ $t("Cancel") }}
        </b-button>
        <b-button
          variant="primary"
          @click="saveConsent"
          :disabled="saving || !isFormValid"
          class="btn-submit-modern px-3"
        >
          <b-spinner v-if="saving" small class="mr-50" />
          <feather-icon v-else icon="CheckIcon" size="16" class="mr-50" />
          {{
            saving
              ? $t("Saving") + "..."
              : isEdit
              ? $t("Update Consent")
              : $t("Save Consent")
          }}
        </b-button>
      </template>
    </b-modal>

    <!-- View Details Modal -->
    <b-modal
      v-model="showViewModal"
      :title="$t('Consent Responses')"
      centered
      size="lg"
      modal-class="modern-modal"
      hide-footer
      body-class="p-0"
    >
      <div class="consent-details-view" v-if="selectedConsent">
        <!-- ── Premium Header ── -->
        <div class="consent-modal-header">
          <!-- Row 1: Title + Status Filter -->
          <div
            class="cmh-top d-flex align-items-center justify-content-between mb-2"
          >
            <div
              class="d-flex align-items-center"
              style="gap: 12px; min-width: 0; flex: 1"
            >
              <div class="title-accent-bar flex-shrink-0"></div>
              <h4
                class="mb-0 font-weight-bolder text-primary text-truncate"
                style="font-size: 1.25rem"
              >
                {{ selectedConsent.title }}
              </h4>
            </div>
            <!-- Status filter at top-right -->
            <div style="width: 200px; flex-shrink: 0">
              <v-select
                v-model="studentStatusFilter"
                :options="statusOptions"
                label="label"
                :reduce="(val) => val.value"
                :placeholder="$t('All Status')"
                class="custom-filter-select"
                append-to-body
                @input="viewConsent(selectedConsent, false)"
              />
            </div>
          </div>

          <!-- Row 2: Meta Tags -->
          <div
            class="cmh-meta-row d-flex align-items-center flex-wrap mb-2"
            style="gap: 10px"
          >
            <span class="cmh-meta-chip" v-if="selectedConsent.className">
              <feather-icon icon="BookOpenIcon" size="14" class="mr-25" />
              {{ selectedConsent.className }}
            </span>
            <span class="cmh-meta-chip" v-if="selectedConsent.metaCampusName">
              <feather-icon icon="MapPinIcon" size="14" class="mr-25" />
              {{ selectedConsent.metaCampusName }}
            </span>
            <span
              class="cmh-meta-chip cmh-desc-chip"
              v-if="selectedConsent.description"
            >
              <feather-icon icon="AlignLeftIcon" size="14" class="mr-25" />
              <span
                class="text-truncate"
                style="
                  max-width: 400px;
                  display: inline-block;
                  vertical-align: middle;
                "
                >{{ selectedConsent.description }}</span
              >
            </span>
          </div>

          <!-- Row 3: Stat Boxes -->
          <div
            class="cmh-stats-row d-flex align-items-center justify-content-end flex-wrap pb-50"
            style="gap: 12px"
          >
            <div class="cmh-stat accepted">
              <feather-icon icon="CheckCircleIcon" size="16" />
              <span class="cmh-stat-num">{{
                selectedConsent.acceptedCount || 0
              }}</span>
              <span class="cmh-stat-label">{{ $t("Approved") }}</span>
            </div>
            <div class="cmh-stat pending">
              <feather-icon icon="ClockIcon" size="16" />
              <span class="cmh-stat-num">{{
                selectedConsent.pendingCount || 0
              }}</span>
              <span class="cmh-stat-label">{{ $t("Pending") }}</span>
            </div>
            <div class="cmh-stat rejected">
              <feather-icon icon="AlertCircleIcon" size="16" />
              <span class="cmh-stat-num">{{
                selectedConsent.rejectedCount || 0
              }}</span>
              <span class="cmh-stat-label">{{ $t("Rejected") }}</span>
            </div>
          </div>
        </div>

        <!-- ── Students Table ── -->
        <div class="consent-modal-body">
          <div v-if="loadingStudents" class="p-2">
            <b-skeleton-table
              :rows="6"
              :columns="5"
              :table-props="{ bordered: false, striped: true }"
            />
          </div>
          <div v-else>
            <b-table
              :items="consentStudents"
              :fields="studentFields"
              hover
              show-empty
              sticky-header="550px"
              :empty-text="$t('No students found for this consent')"
              class="student-response-table mb-0"
              thead-class="consent-modal-thead"
            >
              <template #head()="data">
                <span>{{ $t(data.label) }}</span>
              </template>

              <template #cell(studentName)="{ item }">
                <div class="d-flex align-items-center">
                  <b-avatar
                    size="30"
                    variant="light-primary"
                    class="mr-75 flex-shrink-0"
                  />
                  <span class="font-weight-bold text-dark">{{
                    item.studentName
                  }}</span>
                </div>
              </template>

              <template #cell(status)="{ item }">
                <b-badge
                  :variant="getStatusVariant(item.status)"
                  pill
                  class="px-75"
                >
                  {{ item.status || "Pending" }}
                </b-badge>
              </template>

              <template #cell(date)="{ item }">
                <small v-if="item.date" class="text-muted font-weight-bold">{{
                  formatDate(item.date)
                }}</small>
                <span v-else class="text-muted">—</span>
              </template>
            </b-table>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Save Results Modal -->
    <b-modal
      v-model="showResultModal"
      :title="$t('Save Multi Results Summary')"
      centered
      size="lg"
      modal-class="modern-modal"
      hide-footer
      no-close-on-backdrop
    >
      <div class="result-summary-view p-1">
        <div class="mb-2 modern-detail-header-card compact p-15">
          <div class="d-flex align-items-center mb-50">
            <div class="title-accent-bar mr-50"></div>
            <h4 class="mb-0 text-primary font-weight-bolder">
              {{ $t("Processing Summary") }}
            </h4>
          </div>
          <p class="text-muted small mb-0">
            {{ $t("Overview of consent form distribution across campuses") }}
          </p>
        </div>

        <div class="table-responsive border rounded">
          <b-table
            :items="saveResults"
            :fields="saveResultFields"
            hover
            show-empty
            class="modern-table mb-0"
            responsive
          >
            <template #head()="data">
              <span>{{ $t(data.label) }}</span>
            </template>

            <template #cell(status)="{ item }">
              <b-badge
                :variant="
                  item.status === 'success' ? 'light-success' : 'light-danger'
                "
                pill
                class="px-1"
              >
                <feather-icon
                  :icon="item.status === 'success' ? 'CheckIcon' : 'XIcon'"
                  size="12"
                  class="mr-25"
                />
                {{ item.status }}
              </b-badge>
            </template>
            <template #cell(details)="{ item }">
              <div v-if="item.status === 'success'" class="d-flex flex-column">
                <span class="text-success font-weight-bold">
                  {{ item.savedStudents }} {{ $t("Students Saved") }}
                </span>
                <small class="text-muted"
                  >{{ $t("Out of total") }} {{ item.totalStudents }}</small
                >
              </div>
              <div v-else>
                <span class="text-danger font-weight-bold">{{
                  item.message || $t("Failed")
                }}</span>
              </div>
            </template>
          </b-table>
        </div>

        <div class="d-flex justify-content-end mt-2">
          <b-button
            variant="gradient-primary"
            @click="showResultModal = false"
            class="px-3"
          >
            {{ $t("Close Summary") }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BPagination,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormFile,
  BSpinner,
  BModal,
  VBTooltip,
  BBadge,
  BAvatar,
  BSkeleton,
  BSkeletonTable,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { mapActions } from "vuex";
import axios from "axios";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import Ripple from "vue-ripple-directive";

export default {
  name: "ConsentForm",
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BPagination,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormFile,
    BSpinner,
    BModal,
    vSelect,
    BBadge,
    BAvatar,
    flatPickr,
    BSkeleton,
    BSkeletonTable,
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  data() {
    return {
      rights: {},
      reportDomain: this.$store.state.report.replace(/_/g, ""),
      consents: [],
      summaryData: {
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
      },
      summaryLoading: false,
      filterStatus: "",
      studentStatusFilter: "",
      statusOptions: [
        { label: "All Status", value: "" },
        { label: "Pending", value: "pending" },
        { label: "Approved", value: "approved" },
        { label: "Rejected", value: "rejected" },
      ],
      classesData: [],
      sectionsOptions: [],
      dataLoading: false,
      saving: false,
      uploading: false,
      currentPage: 1,
      perPage: 10,
      totalCount: 0,
      searchQuery: "",
      filterClassID: null,
      searchTimeout: null,
      filterDateRange: null,
      filterDateConfig: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
        mode: "range",
      },
      showModal: false,
      showViewModal: false,
      modalLoadingClasses: false,
      selectedConsent: null,
      isEdit: false,
      campuses: [],
      campusID: null,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "40", value: 40 },
        { label: "80", value: 80 },
        { label: "100", value: 100 },
        { label: "200", value: 200 },
      ],
      fields: [
        {
          key: "sno",
          label: "S.No",
          sortable: false,
          thClass: "text-center",
          tdClass: "text-center align-middle",
          thStyle: "width: 70px",
        },
        {
          key: "title",
          label: "Consent Title",
          sortable: true,
          tdClass: "align-middle font-weight-bold",
        },
        {
          key: "description",
          label: "Description",
          sortable: true,
          tdClass: "align-middle small text-dark",
        },
        {
          key: "class",
          label: "Class",
          sortable: true,
          tdClass: "align-middle",
          thStyle: "width: 120px",
        },
        {
          key: "metaCampusName",
          label: "Campus",
          sortable: true,
          tdClass: "align-middle",
          thStyle: "width: 150px",
        },
        {
          key: "date",
          label: "Created Date",
          sortable: true,
          tdClass: "align-middle",
          thStyle: "width: 150px",
        },
        {
          key: "actions",
          label: "Actions",
          sortable: false,
          thClass: "text-center",
          tdClass: "text-center align-middle",
          thStyle: "width: 100px",
        },
      ],
      studentFields: [
        { key: "grNo", label: "GR No", sortable: true },
        { key: "studentName", label: "Student", sortable: true },
        { key: "sec", label: "Section", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "date", label: "Response Date", sortable: true },
      ],
      loadingStudents: false,
      consentStudents: [],
      form: {
        consent: {
          id: 0,
          tID: 0,
          title: "",
          originalTitle: "", // Added for update tracking
          description: "",
          attachment: "",
          status: "pending",
          campusID: 0,
          date: "",
          originalDate: "", // Added for update tracking
        },
        classID: null,
        sections: [],
        house: "",
        db: "",
        campusID: [],
      },
      showResultModal: false,
      saveResults: [],
      saveResultFields: [
        {
          key: "campusName",
          label: "Campus",
          tdClass: "align-middle font-weight-bold",
        },
        { key: "className", label: "Class", tdClass: "align-middle" },
        {
          key: "status",
          label: "Status",
          tdClass: "align-middle text-center",
          thClass: "text-center",
        },
        {
          key: "details",
          label: "Processing Details",
          tdClass: "align-middle",
        },
      ],
      isHeadOffice: null,
      selectedClasses: [],
      ageOptions: [
        { text: "All", value: "" },
        { text: "Junior", value: "junior" },
        { text: "Senior", value: "senior" },
      ],
    };
  },
  computed: {
    isFormValid() {
      const { title } = this.form.consent;
      return (
        title &&
        title.trim().length > 0 &&
        this.form.campusID &&
        this.form.campusID.length > 0 &&
        // this.form.classID &&
        this.selectedClasses &&
        this.selectedClasses.length > 0 &&
        this.form.sections &&
        this.form.sections.length > 0
      );
    },
    filteredConsents() {
      // With server-side pagination, the 'consents' array already contains the correct items for the page.
      // We just need to return it to maintain table reactivity.
      return this.consents;
    },
    paginatedConsents() {
      // Server-side pagination is used, so the data is already paginated.
      // Do NOT slice the array again.
      return this.filteredConsents;
    },
    filteredTotalCount() {
      return this.totalCount;
    },
    displayedRange() {
      const start = (this.currentPage - 1) * this.perPage + 1;
      const count = this.consents ? this.consents.length : 0;
      const end = Math.min(
        (this.currentPage - 1) * this.perPage + count,
        this.totalCount
      );
      return { start, end: end > 0 ? end : 0 };
    },
  },
  created() {
    this.setupRights();

    if (!this.rights || !this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    this.LoadSettings();
    this.loadCampuses().then(() => {
      this.fetchClasses(this.$store.state.userData.cId);
    });
    this.loadConsents();
  },
  mounted() {
    window.addEventListener("resize", this.updateScrollbarWidth);
    // Initial call
    setTimeout(this.updateScrollbarWidth, 1000);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateScrollbarWidth);
  },
  methods: {
    ...mapActions({ get: "get", post: "post", del: "delete" }),

    getLabel(key) {
      const isHappiness = this.reportDomain == "myskoolhappinessfoundation";
      const map = {
        class: isHappiness ? "group" : "class",
        classes: isHappiness ? "groups" : "classes",
        section: isHappiness ? "level" : "section",
        sections: isHappiness ? "levels" : "sections",
      };
      const result = map[key.toLowerCase()] ?? key.toLowerCase();
      const isCapitalized =
        key[0] === key[0].toUpperCase() && key[0] !== key[0].toLowerCase();
      return isCapitalized
        ? result.charAt(0).toUpperCase() + result.slice(1)
        : result;
    },

    setupRights() {
      let right = {};
      if (this.$store.state.menu) {
        this.$store.state.menu.forEach((el) => {
          if (el.children) {
            el.children.forEach((ch) => {
              right[ch.route] = {
                view: ch.view,
                add: ch.add,
                edit: ch.edit,
                delete: ch.delete,
                assignSections: ch.assignSections,
              };
            });
          }
        });
      }
      this.$store.commit("setRights", right);
      this.rights = this.$store.state.rights[this.$route.name];
    },

    async LoadSettings() {
      let res = await this.post({
        url:
          this.$store.state.domain +
          "Settings/getSettings?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        body: ["head_office"],
        context: this,
        token: this.$store.state.userData.token,
        subjects: true,
      });

      this.isHeadOffice = res.find((el) => el.key == "head_office");
    },

    async loadCampuses() {
      const obj = {
        url:
          this.$store.state.domain +
          "campuses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      const res = await this.get(obj);
      if (res && res.data && Array.isArray(res.data.data)) {
        this.campuses = res.data.data;
      } else if (res && res.data && Array.isArray(res.data)) {
        this.campuses = res.data;
      } else if (Array.isArray(res)) {
        this.campuses = res;
      } else {
        this.campuses = [];
      }

      // Optional: select current campus if list is not empty
      if (this.campuses && this.campuses.length > 0 && !this.campusID) {
        // Ensure the current user's campus is selected by default or 'All' if applicable
        // Often HeadOffice users want to see 'All' first or their main branch.
        // For now, let's keep it null (All? or nothing selected?)
        // If the API requires a specific ID, we should select one.
        // Since original call used userData.cId, we can default to that.
        // this.campusID = this.$store.state.userData.cId
      }
    },

    async loadConsents() {
      this.dataLoading = true;
      this.summaryLoading = true;
      try {
        const cID = this.campusID || this.$store.state.userData.cId;

        let url = `${this.$store.state.domain}Consents/GetConsentsSummary?db=${this.$store.state.userData.db}&campusID=${cID}&page=${this.currentPage}&pageSize=${this.perPage}`;

        if (this.searchQuery)
          url += `&searchTerm=${encodeURIComponent(this.searchQuery)}`;
        if (this.filterClassID) url += `&classID=${this.filterClassID}`;
        if (this.filterStatus) url += `&status=${this.filterStatus}`;
        if (this.filterDateRange) {
          const dates = this.filterDateRange.split(" to ");
          if (dates[0]) url += `&fromDate=${dates[0]}`;
          if (dates[1]) url += `&toDate=${dates[1]}`;
        }

        const res = await this.get({
          url: url,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        // console.log("Load Consents Response:", res);

        let items = [];
        let count = 0;
        let summaryObj = null;

        if (res && Array.isArray(res.data)) {
          items = res.data;
          count = res.totalCount;
          summaryObj = res.summary;
        } else if (res && res.data && Array.isArray(res.data.data)) {
          items = res.data.data;
          count = res.data.totalCount;
          summaryObj = res.data.summary;
        } else if (Array.isArray(res)) {
          items = res;
          count = res.length;
        }

        this.consents = items || [];
        this.totalCount = count || 0;

        if (summaryObj) {
          const approved = summaryObj.approved ?? summaryObj.accepted ?? 0;
          const rejected = summaryObj.rejected ?? 0;
          this.summaryData = {
            total: Number(summaryObj.total) || 0,
            pending: Number(summaryObj.pending) || 0,
            approved: Number(approved) || 0,
            rejected: Number(rejected) || 0,
          };
        }

        this.updateScrollbarWidth();
      } catch (error) {
        console.error("Load Consents Error:", error);
      } finally {
        this.dataLoading = false;
        this.summaryLoading = false;
      }
    },

    // Merged into loadConsents to avoid double fetching
    async loadConsentSummary() {
      // Placeholder if needed explicitly
    },

    async fetchClasses(campusID = null) {
      this.modalLoadingClasses = true;
      try {
        let cID = this.$store.state.userData.cId;
        if (campusID) {
          cID = Array.isArray(campusID) ? campusID[0] : campusID;
        }

        const res = await this.get({
          url: `${this.$store.state.domain}myclasses/LoadClasses?db=${this.$store.state.userData.db}&cID=${cID}&dID=0`,
          token: this.$store.state.userData.token,
        });

        // console.log("LoadClasses API Response:", res);

        // Handle different response structures
        let classesArray = [];

        // Check if response has nested data.data structure
        if (res && res.data && res.data.data && Array.isArray(res.data.data)) {
          classesArray = res.data.data;
        }
        // Check if response has data array directly
        else if (res && res.data && Array.isArray(res.data)) {
          classesArray = res.data;
        }
        // Check if response is array directly
        else if (Array.isArray(res)) {
          classesArray = res;
        }

        // console.log("Classes Array:", classesArray);

        if (classesArray.length > 0) {
          // Map to a flatter structure for easier consumption
          this.classesData = classesArray.map((item) => {
            // Use the sections array directly from the API response
            const cls = item.cls || {};
            const sectionsList = item.sections || [];

            return {
              id: cls.id,
              name: cls.name,
              sections: sectionsList,
            };
          });

          // console.log("Mapped classesData:", this.classesData);
        } else {
          console.warn("No classes found in response");
        }
      } catch (error) {
        console.error("Fetch Classes Error:", error);
      } finally {
        this.modalLoadingClasses = false;
      }
    },

    onClassChange(val) {
      this.sectionsOptions = [];
      // all classes selected
      if (this.selectedClasses.at(-1) == 0) {
        this.selectedClasses = [0];
        this.form.sections = [0];
      } else {
        this.selectedClasses = this.selectedClasses.filter((el) => el != 0);
        // if only one class selected then load its sections
        if (this.selectedClasses.length == 1) {
          this.form.sections = [0];
          const classObj = this.classesData.find(
            (c) => c.id === this.selectedClasses[0]
          );
          if (classObj && classObj.sections) {
            this.sectionsOptions = classObj.sections;
          }
        } else {
          this.form.sections = [0];
        }
      }
    },
    onSectionChange() {
      if (this.form.sections.at(-1) == 0) {
        this.form.sections = [0];
      } else {
        this.form.sections = this.form.sections.filter((el) => el != 0);
      }
    },

    syncScroll() {
      if (this.$refs.tableWrapper && this.$refs.topScrollbar) {
        const tableWrapper = this.$refs.tableWrapper;
        const topScrollbar = this.$refs.topScrollbar;
        topScrollbar.scrollLeft = tableWrapper.scrollLeft;
      }
    },

    updateScrollbarWidth() {
      this.$nextTick(() => {
        if (this.$refs.tableWrapper && this.$refs.topScrollbar) {
          const tableWrapper = this.$refs.tableWrapper;
          const topScrollbar = this.$refs.topScrollbar;
          const scrollContent = topScrollbar.querySelector(
            ".table-scrollbar-content"
          );

          if (scrollContent) {
            // Use table dynamic scrollWidth
            const totalWidth = tableWrapper.scrollWidth;
            scrollContent.style.width = totalWidth + "px";

            // Sync scroll on top scrollbar drag
            topScrollbar.onscroll = () => {
              if (tableWrapper.scrollLeft !== topScrollbar.scrollLeft) {
                tableWrapper.scrollLeft = topScrollbar.scrollLeft;
              }
            };
          }
        }
      });
    },

    removeAttachment() {
      this.form.consent.attachment = "";
    },

    getFileName(url) {
      if (!url) return "";
      return url.split("/").pop();
    },

    getStatusVariant(status) {
      switch (status?.toLowerCase()) {
        case "active":
          return "light-success";
        case "pending":
          return "light-warning";
        case "expired":
          return "light-danger";
        default:
          return "light-primary";
      }
    },

    onSearchChange() {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Debounce search to avoid too many API calls
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadConsents();
      }, 500);
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadConsents();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadConsents();
    },

    onPrint() {
      const cID = this.campusID || this.$store.state.userData.cId;
      const db = this.$store.state.userData.db;

      let url = `${window.location.origin}/reports/consent-print?db=${db}&cId=${cID}`;

      if (this.searchQuery)
        url += `&searchTerm=${encodeURIComponent(this.searchQuery)}`;
      if (this.filterClassID) url += `&classID=${this.filterClassID}`;
      if (this.filterStatus) url += `&status=${this.filterStatus}`;
      if (this.filterDateRange) {
        const dates = this.filterDateRange.split(" to ");
        if (dates[0]) url += `&fromDate=${dates[0]}`;
        if (dates[1]) url += `&toDate=${dates[1]}`;
      }

      window.open(url, "_blank");
    },

    onPrintSingle(item) {
      const cID =
        item.campusID || this.campusID || this.$store.state.userData.cId;
      const db = this.$store.state.userData.db;

      let url = `${window.location.origin}/reports/consent-print?db=${db}&cId=${cID}`;

      url += `&searchTerm=${encodeURIComponent(item.title)}`;
      if (item.classID) url += `&classID=${item.classID}`;
      if (item.date) {
        const dateOnly = item.date.split("T")[0];
        url += `&fromDate=${dateOnly}&toDate=${dateOnly}`;
      }

      window.open(url, "_blank");
    },

    async onExport() {
      try {
        this.dataLoading = true;
        const cID = this.campusID || this.$store.state.userData.cId;
        const db = this.$store.state.userData.db;

        let url = `${this.$store.state.domain}Consents/GetAllConsents?db=${db}&campusID=${cID}&page=1&pageSize=10000`;

        if (this.searchQuery)
          url += `&searchTerm=${encodeURIComponent(this.searchQuery)}`;
        if (this.filterClassID) url += `&classID=${this.filterClassID}`;
        if (this.filterStatus) url += `&status=${this.filterStatus}`;
        if (this.filterDateRange) {
          const dates = this.filterDateRange.split(" to ");
          if (dates[0]) url += `&fromDate=${dates[0]}`;
          if (dates[1]) url += `&toDate=${dates[1]}`;
        }

        const res = await this.get({
          url: url,
          token: this.$store.state.userData.token,
        });

        // Robust data extraction similar to loadConsents
        let data = [];
        if (res && Array.isArray(res.data)) {
          data = res.data;
        } else if (res && res.data && Array.isArray(res.data.data)) {
          data = res.data.data;
        } else if (Array.isArray(res)) {
          data = res;
        }

        if (data.length > 0) {
          const excelData = data.map((item, index) => ({
            "#": index + 1,
            "Consent Title": item.title || "-",
            "Student Name": item.studentName || "-",
            "GR No (CID)": item.grNo || "-",
            Class: item.clsName || "-",
            Section: item.sec || "-",
            "Phone No": item.phoneNumber || "-",
            Status: item.status || "-",
            Campus: item.metaCampusName || "-",
            Date: item.date ? new Date(item.date).toLocaleDateString() : "-",
          }));

          const worksheet = XLSX.utils.json_to_sheet(excelData);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, "Consents");
          XLSX.writeFile(workbook, "Consents_Report.xlsx");
        } else {
          this.$bvToast.toast("No records found to export", {
            variant: "warning",
            title: "Export",
          });
        }
      } catch (error) {
        console.error("Export Error:", error);
        this.$bvToast.toast("Error generating Excel report", {
          variant: "danger",
          title: "Export",
        });
      } finally {
        this.dataLoading = false;
      }
    },

    closePageSizeDropdown() {
      this.$nextTick(() => {
        if (this.$refs.pageSizeSelect) {
          const toggle = this.$refs.pageSizeSelect.$el?.querySelector(
            ".vs__dropdown-toggle"
          );
          if (toggle) {
            toggle.blur();
            setTimeout(() => {
              document.body.click();
            }, 100);
          }
        }
      });
    },

    openAddModal() {
      this.isEdit = false;
      this.resetForm();
      this.form.campusID = [];
      this.selectedClasses = [];
      this.form.sections = [];

      // Default to user's campus if available and populate array
      if (this.$store.state.userData.cId) {
        this.form.campusID = [this.$store.state.userData.cId];
        this.fetchClasses(this.form.campusID);
      } else if (this.campuses.length > 0) {
        // Fallback: select first campus
        this.form.campusID = [this.campuses[0].id];
        this.fetchClasses(this.form.campusID);
      }

      this.showModal = true;
    },

    fetchClassesForModal() {
      if (this.form.campusID && this.form.campusID.length > 0) {
        this.fetchClasses(this.form.campusID);
      } else {
        this.classesData = [];
        this.form.classID = [];
        this.form.sections = [];
      }
    },

    async viewConsent(item, resetFilter = false) {
      if (resetFilter) this.studentStatusFilter = "";
      this.selectedConsent = { ...item };
      this.showViewModal = true;
      this.loadingStudents = true;
      this.consentStudents = [];

      try {
        const cID =
          item.campusID || this.campusID || this.$store.state.userData.cId;
        // API: GetConsentStudents(db, campusID, title, date, classID)
        // Note: Added classID. If item doesn't have classID directly, it should be part of the summary object.
        const res = await this.get({
          url: `${this.$store.state.domain}Consents/GetConsentStudents?db=${
            this.$store.state.userData.db
          }&campusID=${cID}&title=${encodeURIComponent(item.title)}&date=${
            item.date
          }&classID=${item.classID || 0}${
            this.studentStatusFilter
              ? `&status=${this.studentStatusFilter}`
              : ""
          }`,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        const responseData = res.data || res;
        if (responseData && Array.isArray(responseData.data)) {
          this.consentStudents = responseData.data;
        } else if (Array.isArray(responseData)) {
          this.consentStudents = responseData;
        }
      } catch (error) {
        console.error("Fetch Students Error:", error);
      } finally {
        this.loadingStudents = false;
      }
    },

    async editConsent(item) {
      this.isEdit = true;
      this.saving = true; // Use saving to block interactions while loading details

      try {
        const cID =
          item.campusID || this.campusID || this.$store.state.userData.cId;
        // fetch student list to extract details
        const res = await this.get({
          url: `${this.$store.state.domain}Consents/GetConsentStudents?db=${
            this.$store.state.userData.db
          }&campusID=${cID}&title=${encodeURIComponent(item.title)}&date=${
            item.date
          }&classID=${item.classID || 0}`,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        const responseData = res.data || res;
        const students =
          (Array.isArray(responseData) ? responseData : responseData.data) ||
          [];

        // Populate basic form data from item
        this.form.consent = {
          id: item.id || 0,
          title: item.title,
          description: item.description,
          status: item.status || "pending",
          tID: item.tID || this.$store.state.userData.userID,
          date: item.date, // Store current date for reference
          originalTitle: item.title, // Store original for update key
          originalDate: item.date, // Store original for update key
        };

        if (students.length > 0) {
          // 1. Identify Unique Campus IDs from students
          // Note: Since students might not have campusID, but they have clsName.
          // Usually a summary row is for ONE campus.
          this.form.campusID = [cID];

          // 2. Refresh classes for that campus
          await this.fetchClasses(this.form.campusID);

          // 3. Find the Class ID by name (className from summary or student)
          const targetClassName = item.className || students[0].clsName;
          const foundClass = this.classesData.find(
            (c) => c.name === targetClassName
          );

          if (foundClass) {
            this.form.classID = foundClass.id;
            this.selectedClasses = [foundClass.id];
            // 4. Refresh sections for this class
            await this.onClassChange(foundClass.id);

            // 5. Build unique sections list from students
            const uniqueSecNames = [...new Set(students.map((s) => s.sec))];
            this.form.sections = this.sectionsOptions
              .filter((opt) => uniqueSecNames.includes(opt.section))
              .map((opt) => opt.id);
          }
        }

        this.showModal = true;
      } catch (error) {
        console.error("Edit Data Fetch Error:", error);
        this.$bvToast.toast(this.$t("Could not fetch configuration details"), {
          variant: "danger",
        });
      } finally {
        this.saving = false;
      }
    },

    async saveConsent() {
      this.saving = true;
      try {
        let className = "0";
        if (this.selectedClasses.length > 0 && this.selectedClasses[0] != 0) {
          className = this.classesData
            .filter((s) => this.selectedClasses.includes(s.id))
            .map((s) => s.name)
            .join(",");
        }

        let sectionName = "0";
        if (
          this.form.sections &&
          this.form.sections.length > 0 &&
          this.form.sections[0] != 0
        ) {
          sectionName = this.sectionsOptions
            .filter((s) => this.form.sections.includes(s.id))
            .map((s) => s.section)
            .join(",");
        }
        // console.log(className, sectionName);

        const cIDs = this.form.campusID.join(",");

        let payload = {};

        if (this.isEdit) {
          // Update Payload Structure (UpdateMulti)
          const originalDate = this.form.consent.date; // Assuming we stored original date when editing
          payload = {
            db: this.$store.state.userData.db,
            campusID: cIDs
              ? parseInt(cIDs.split(",")[0])
              : this.$store.state.userData.cId, // Take first campus
            classID: this.form.classID,
            oldTitle:
              this.form.consent.originalTitle || this.form.consent.title, // Need original title
            oldDate: this.form.consent.originalDate || new Date().toISOString(), // Need original date
            newTitle: this.form.consent.title,
            newDescription: this.form.consent.description,
            // newAttachment: this.form.consent.attachment || undefined // Don't pass if empty? User said "dont pass the atchemnt for it"
          };
          if (this.form.consent.attachment) {
            payload.newAttachment = this.form.consent.attachment;
          }
        } else {
          // Create Payload Structure (saveMulti)
          payload = {
            consent: {
              id: 0,
              title: this.form.consent.title,
              description: this.form.consent.description,
              attachment: this.form.consent.attachment,
              status: "pending",
              tID: this.$store.state.userData.userID,
            },
            cIDs: cIDs,
            className: className,
            sectionName: sectionName,
            db: this.$store.state.userData.db,
            house: this.form.house,
          };
        }

        const response = await this.post({
          url: this.isEdit
            ? `${this.$store.state.domain}Consents/UpdateMulti`
            : `${this.$store.state.domain}Consents/saveMulti`,
          body: payload,
          token: this.$store.state.userData.token,
          message: this.isEdit
            ? this.$t("Consent details updated")
            : this.$t("New consent form added"),
          context: this,
          allResponse: true, // This ensures we get the response object back
        });

        if (response && response.status === "success") {
          // If it was a multi-campus save from Head Office, show the detailed results modal
          // The data array contains the per-campus results
          if (
            !this.isEdit &&
            this.isHeadOffice &&
            Array.isArray(response.data)
          ) {
            this.saveResults = response.data;
            this.showResultModal = true;
          } else {
            // Standard success message for single campus or edits
            const msg = this.isEdit
              ? this.$t("Consent details updated")
              : this.$t("New consent form added");
            this.$bvToast.toast(msg, { variant: "success" });
          }

          this.showModal = false;
          this.loadConsents();
        }
      } catch (error) {
        console.error("Save Consent Error:", error);
        this.$bvToast.toast(this.$t("Error saving consent data"), {
          variant: "danger",
        });
      } finally {
        this.saving = false;
      }
    },

    async deleteConsent(item) {
      const result = await Swal.fire({
        title: this.$t("Delete Consent?"),
        text: this.$t("This will remove the consent form permanently."),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("Yes, delete it"),
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: "btn btn-outline-secondary ml-1",
        },
        buttonsStyling: false,
      });

      if (result.isConfirmed) {
        try {
          const cID =
            item.campusID || this.campusID || this.$store.state.userData.cId;
          const url = `${this.$store.state.domain}Consents/DeleteMulti?db=${
            this.$store.state.userData.db
          }&campusID=${cID}&title=${encodeURIComponent(item.title)}&date=${
            item.date
          }&classID=${item.classID || 0}`;

          const success = await this.del({
            url: url,
            token: this.$store.state.userData.token,
            message: this.$t("Consent form deleted successfully"),
            context: this,
          });

          if (success) {
            this.loadConsents();
          }
        } catch (error) {
          console.error("Delete Error:", error);
          this.$bvToast.toast(this.$t("Deletion failed"), {
            variant: "danger",
          });
        }
      }
    },

    resetForm() {
      this.form = {
        consent: {
          id: 0,
          tID: this.$store.state.userData.userID,
          title: "",
          description: "",
          attachment: "",
          status: "pending",
          campusID: this.$store.state.userData.cId,
        },
        classID: null,
        sections: [],
        house: "",
        db: this.$store.state.userData.db,
      };
      this.sectionsOptions = [];
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    downloadAttachment(item) {
      if (item.attachment) {
        window.open(item.attachment, "_blank");
      } else {
        this.$bvToast.toast(this.$t("No attachment available"), {
          variant: "warning",
        });
      }
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>

<style lang="scss" scoped>
.consents-list-card {
  border: none;
  box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.05);
  border-radius: 1.5rem;
  overflow: hidden;
  position: relative;
  background: #fff;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 30px 0 rgba(0, 0, 0, 0.08);
  }
}

.gradient-line-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, #7367f0 0%, #ce9ffc 100%);
}

.consent-title {
  font-weight: 800;
  color: #2c3e50;
  letter-spacing: -0.5px;
}

.modern-input,
.modern-textarea,
.modern-select {
  border-radius: 0.75rem;
  border: 1px solid #ebedf0;
  transition: all 0.2s ease;

  &:focus {
    border-color: #7367f0;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.1);
  }
}

.action-btn-filter {
  border-radius: 0.75rem;
  padding: 0.6rem 1.5rem;
  font-weight: 600;
  box-shadow: 0 4px 14px 0 rgba(115, 103, 240, 0.3);
}

.pagination-controls-top {
  background: #f8f9fa;
  padding: 0.5rem 1rem;
  border-radius: 0.75rem;
}

.btn-modern-primary {
  background: linear-gradient(135deg, #7367f0 0%, #ce9ffc 100%);
  border: none;
  border-radius: 0.75rem;
  font-weight: 600;
  padding: 0.6rem 2rem;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(115, 103, 240, 0.4);
  }
}

.btn-modern-cancel {
  border-radius: 0.75rem;
  border: 1px solid #ebedf0;
  color: #6e6b7b;
  font-weight: 600;
  padding: 0.6rem 2rem;
}

.skeleton-row {
  .skeleton-cell {
    padding: 1.5rem 1rem;
  }
  .skeleton-loader {
    height: 20px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 4px;
  }
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.modern-card {
  border: none;
  box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.05);
  border-radius: 1.5rem;
  overflow: hidden;
  position: relative;
  background: #fff;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 30px 0 rgba(0, 0, 0, 0.08);
  }
}

.gradient-line-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, #7367f0 0%, #ce9ffc 100%);
}

.card-title-modern {
  font-weight: 800;
  color: #2c3e50;
  letter-spacing: -0.5px;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.modern-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem 1.75rem 1.5rem 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 1.5rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: visible;
  z-index: 1;

  .gradient-line-wrapper {
    position: absolute;
    top: 0;
    left: 0.75rem;
    right: 0.75rem;
    width: calc(100% - 1.5rem);
    height: 5px;
    overflow: hidden;
    border-radius: 10px 10px 0 0;
    z-index: 1;
    pointer-events: none;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 5px;
      background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
      border-radius: 10px 10px 0 0;
    }
  }

  &:hover {
    box-shadow: 0 15px 40px rgba(115, 103, 240, 0.2);
    transform: translateY(-2px);
  }
}

.card-title-modern {
  font-size: 1.25rem;
  font-weight: 800;
  color: #2d1b69;
  letter-spacing: -0.3px;
}

.table-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0edff;
  margin-bottom: 1rem;

  small,
  p {
    font-size: 0.85rem;
    color: #4b5563;
    font-weight: 600;
  }
}

.pagination-controls-top {
  padding: 0.75rem 1.25rem;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  gap: 1rem;

  @media (max-width: 992px) {
    flex-wrap: wrap;
    gap: 0.75rem;

    .pagination-count-display {
      order: 3;
      width: 100%;
      margin: 0.5rem 0 0 0;
      text-align: center;
    }
  }
}

.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .page-size-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #64748b;
    margin: 0;
    white-space: nowrap;
  }
}

.page-size-select-compact {
  min-width: 75px;
  width: 75px;

  ::v-deep .vs__dropdown-toggle {
    height: 32px !important;
    min-height: 32px !important;
    border-radius: 6px !important;
    border: 1.5px solid #e0d8ff !important;
    padding: 0.25rem 0.5rem !important;
    background: #fff !important;
    font-size: 0.8125rem !important;

    &:hover {
      border-color: #b8a9ff !important;
    }

    &:focus-within {
      border-color: #7c5dfa !important;
      box-shadow: 0 0 0 2px rgba(124, 93, 250, 0.1) !important;
    }
  }

  ::v-deep .vs__selected {
    margin: 0 !important;
    padding: 0 !important;
    color: #2d1b69 !important;
    font-weight: 600 !important;
    font-size: 0.8125rem !important;
  }

  ::v-deep .vs__dropdown-menu {
    border-radius: 8px !important;
    border: 1.5px solid #e0d8ff !important;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15) !important;
  }

  ::v-deep .vs__dropdown-option--highlight {
    background-color: #f3f0ff !important;
    color: #7c5dfa !important;
  }
}

.pagination-count-display {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  margin: 0 1rem;

  .count-text {
    font-size: 0.8125rem;
    color: #64748b;
    font-weight: 500;

    strong {
      color: #2d1b69;
      font-weight: 700;
    }
  }
}

.pagination-wrapper-compact {
  display: flex;
  align-items: center;

  ::v-deep .pagination {
    margin-bottom: 0;

    .page-link {
      padding: 0.375rem 0.625rem;
      font-size: 0.8125rem;
      line-height: 1.5;
      border-radius: 6px;
      margin: 0 0.125rem;
      border: 1px solid #e0d8ff;
      color: #64748b;
      font-weight: 500;
      min-width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        background: #f3f0ff;
        border-color: #b8a9ff;
        color: #7c5dfa;
      }
    }

    .page-item.active .page-link {
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      border-color: #7c5dfa;
      color: #fff;
      font-weight: 600;
      box-shadow: 0 2px 8px rgba(124, 93, 250, 0.3);
    }
  }
}

.modern-table {
  margin-top: 1rem;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;

  ::v-deep th {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: none;
    font-weight: 700;
    color: #1e293b;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    padding: 0.75rem 0.875rem;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
  }

  ::v-deep td {
    vertical-align: middle;
    border-top: 1px solid #f1f5f9;
    border-bottom: 1px solid #f8fafc;
    padding: 0.75rem 0.875rem;
    background: #ffffff;
    font-size: 0.875rem;
    color: #1e293b;
  }

  ::v-deep tbody tr:hover {
    background: #f1f5f9 !important;

    td {
      background: transparent !important;
      color: #1e293b;
      font-weight: 500;
    }
  }
}

.btn-add-modern {
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%) !important;
  border: none !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  padding: 0.6rem 1.25rem !important;
  box-shadow: 0 4px 12px rgba(124, 93, 250, 0.25) !important;
  transition: all 0.3s ease !important;
  color: white !important;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(124, 93, 250, 0.35) !important;
    background: linear-gradient(135deg, #6d4cdb 0%, #8b6fff 100%) !important;
  }
}

.result-summary-view {
  .modern-table {
    border: none;

    ::v-deep th {
      background: #f8faff;
      color: #5e5873;
      font-weight: 600;
      text-transform: uppercase;
      font-size: 0.8rem;
    }

    ::v-deep td {
      padding: 1rem 0.875rem;
    }
  }

  .border {
    border-color: #ebe9f1 !important;
  }
}

.modern-detail-header-card.p-15 {
  padding: 1.25rem !important;
  background: #f8faff;
  border-radius: 12px;
  border-left: 4px solid #7367f0;
}

/* ---- Premium Filter Inputs (journalAdvance pattern) ---- */
.modern-input {
  border-radius: 10px !important;
  border: 1.5px solid #e0d8ff !important;
  padding: 0.625rem 0.875rem !important;
  height: 44px;
  transition: all 0.2s ease;
  font-weight: 500;
  color: #1e293b;

  &:focus {
    border-color: #7c5dfa !important;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1) !important;
    outline: none;
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }
}

/* Custom Filter Select - Full width, 44px height, no double border */
.custom-filter-select {
  width: 100% !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  margin: 0 !important;
}
.custom-filter-select ::v-deep .vs__dropdown-toggle {
  background: #fff !important;
  border: 1.5px solid #e0d8ff !important;
  border-radius: 10px !important;
  height: 44px !important;
  min-height: 44px !important;
  padding: 0 12px !important;
  box-shadow: none !important;
  margin: 0 !important;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
}
.custom-filter-select ::v-deep .vs__dropdown-toggle:hover {
  border-color: #b8a9ff !important;
}
.custom-filter-select ::v-deep .vs__dropdown-toggle:focus-within {
  border-color: #7c5dfa !important;
  box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1) !important;
}
.custom-filter-select ::v-deep .vs__dropdown-menu {
  border: 1.5px solid #e0d8ff !important;
  border-radius: 10px !important;
  box-shadow: 0 10px 25px rgba(124, 93, 250, 0.12) !important;
  padding: 6px !important;
  margin-top: 4px !important;
}
.custom-filter-select ::v-deep .vs__dropdown-option {
  padding: 9px 12px !important;
  border-radius: 7px !important;
  margin-bottom: 2px !important;
  color: #475569 !important;
  font-weight: 500 !important;
  font-size: 0.875rem !important;
}
.custom-filter-select ::v-deep .vs__dropdown-option:hover,
.custom-filter-select ::v-deep .vs__dropdown-option--highlight {
  background: #f3f0ff !important;
  color: #7c5dfa !important;
}
.custom-filter-select ::v-deep .vs__selected-options {
  display: flex !important;
  align-items: center !important;
  flex-grow: 1 !important;
  padding: 0 !important;
  margin: 0 !important;
  min-width: 0 !important;
  overflow: hidden !important;
}
.custom-filter-select ::v-deep .vs__selected {
  position: relative !important;
  z-index: 5 !important;
  margin: 0 !important;
  padding: 0 !important;
  color: #2d1b69 !important;
  font-weight: 600 !important;
  font-size: 0.875rem !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  max-width: 100% !important;
}
.custom-filter-select ::v-deep .vs__search {
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  color: #2d1b69 !important;
  font-size: 0.875rem !important;
  height: 100% !important;
}
.custom-filter-select ::v-deep .vs__search::placeholder {
  color: #9ca3af !important;
  font-weight: 400 !important;
  opacity: 1 !important;
}
.custom-filter-select ::v-deep .vs__actions {
  padding: 0 4px 0 8px !important;
  display: flex !important;
  align-items: center !important;
}
.custom-filter-select ::v-deep .vs__clear {
  margin-right: 2px !important;
}

.filter-control-modern {
  border-radius: 10px;
  border: 1.5px solid #e0d8ff;
  padding: 0.75rem 0.875rem;
  background: #fff;
  transition: all 0.2s ease;
  color: #1e293b;
  font-weight: 500;

  &:hover {
    border-color: #b8a9ff;
  }

  &:focus,
  &:focus-within {
    border-color: #7c5dfa;
    box-shadow: none !important;
    outline: none;
  }

  &.v-select {
    padding: 0 !important;
    ::v-deep .vs__dropdown-toggle {
      border: none !important;
      padding: 6px 4px !important;
      background: transparent !important;
    }
  }

  &::placeholder {
    color: #94a3b8;
    font-weight: 400;
  }
}

.action-btn-view {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-weight: 600;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%) !important;
  border: none !important;
  color: #ffffff !important;
  box-shadow: 0 2px 8px rgba(124, 93, 250, 0.3);

  ::v-deep svg {
    color: #ffffff !important;
    stroke: #ffffff !important;
  }

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #6d4cdb 0%, #8b6fff 100%) !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

.action-btn-outline-success {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-weight: 600;
  background: #ffffff !important;
  border: 2px solid #10b981 !important;
  color: #10b981 !important;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.15);

  ::v-deep svg {
    color: #10b981 !important;
    stroke: #10b981 !important;
  }

  &:hover:not(:disabled) {
    background: #f0fdf4 !important;
    border-color: #059669 !important;
    color: #059669 !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);

    ::v-deep svg {
      color: #059669 !important;
      stroke: #059669 !important;
    }
  }
}

.action-btn-outline-danger {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-weight: 600;
  background: #ffffff !important;
  border: 2px solid #ef4444 !important;
  color: #ef4444 !important;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.15);

  ::v-deep svg {
    color: #ef4444 !important;
    stroke: #ef4444 !important;
  }

  &:hover:not(:disabled) {
    background: #fef2f2 !important;
    border-color: #dc2626 !important;
    color: #dc2626 !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);

    ::v-deep svg {
      color: #dc2626 !important;
      stroke: #dc2626 !important;
    }
  }
}

.table-responsive {
  overflow-x: auto !important;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;

    &:hover {
      background: #94a3b8;
    }
  }
}

/* Modal Styling */
.modern-input-field,
.modern-textarea-field {
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  padding: 0.6rem 0.8rem;
  &:focus {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.1);
  }
}

.modern-select-field {
  ::v-deep .vs__dropdown-toggle {
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    padding: 2px 0;
  }
}

.btn-submit-modern {
  background: #7367f0;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  padding: 0.6rem 1.5rem;
  color: white;
}

.btn-cancel-modern {
  border-radius: 8px;
  font-weight: 600;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.file-preview-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.8rem 1rem;
  background-color: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02);
}

.file-icon-box {
  width: 40px;
  height: 40px;
  background-color: rgba(115, 103, 240, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modern-file-input {
  ::v-deep .custom-file-label {
    border-radius: 8px;
    height: calc(1.5em + 1.2rem + 2px);
    padding: 0.6rem 1rem;
    &::after {
      height: 100%;
      border-radius: 0 8px 8px 0;
      padding: 0.6rem 1rem;
    }
  }
}

/* Professional Summary Cards */
.professional-stat-card {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #ebe9f1;
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05);
  padding: 1.25rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 25px 0 rgba(0, 0, 0, 0.1);
  }

  .stat-body {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
  }

  .stat-info {
    display: flex;
    flex-direction: column;
  }

  .stat-label {
    font-size: 0.8rem;
    font-weight: 700;
    color: #4b5563;
    margin-bottom: 0.25rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .stat-value {
    font-size: 1.8rem;
    font-weight: 700;
    margin: 0;
    line-height: 1.2;
  }

  .stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 24px;
      height: 24px;
    }
  }

  .stat-footer {
    font-size: 0.75rem;
    padding: 0.35rem 0.75rem;
    border-radius: 6px;
    font-weight: 500;
    width: fit-content;
  }

  &.border-primary {
    border-left: 5px solid #7367f0;
  }
  &.border-success {
    border-left: 5px solid #28c76f;
  }
  &.border-warning {
    border-left: 5px solid #ff9f43;
  }
  &.border-danger {
    border-left: 5px solid #ea5455;
  }

  &.skeleton {
    background: #f8fafc;
    border: none;
    min-height: 110px;
    overflow: hidden;
  }
}

.bg-light-primary {
  background-color: rgba(115, 103, 240, 0.12) !important;
  color: #7367f0 !important;
}
.bg-light-success {
  background-color: rgba(40, 199, 111, 0.12) !important;
  color: #28c76f !important;
}
.bg-light-warning {
  background-color: rgba(255, 159, 67, 0.12) !important;
  color: #ff9f43 !important;
}
.bg-light-danger {
  background-color: rgba(234, 84, 85, 0.12) !important;
  color: #ea5455 !important;
}

.text-primary {
  color: #7367f0 !important;
}
.text-success {
  color: #28c76f !important;
}
.text-warning {
  color: #ff9f43 !important;
}
.text-danger {
  color: #ea5455 !important;
}

.glass-summary-card {
  display: none !important;
}

.summary-card-modern {
  display: none !important; /* Hide old cards */
}

/* Modal Premium Redesign */
.modern-detail-header-card {
  background: #ffffff;
  border: 1px solid #e0e7ff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  overflow: hidden;

  .header-top-section {
    background: linear-gradient(to right, #ffffff, #f8faff);
  }

  .header-right-stats {
    gap: 16px;
  }

  .title-accent-bar {
    width: 6px;
    height: 28px;
    background: linear-gradient(to bottom, #7367f0, #ce9ffc);
    border-radius: 4px;
  }

  .title-text {
    letter-spacing: -0.5px;
    color: #2d1b69;
    font-size: 1.25rem;
  }

  .header-meta-info {
    margin-left: 10px;
    gap: 8px;
    display: flex;

    .meta-item {
      display: flex;
      align-items: center;
      background: #f1f5f9;
      padding: 3px 10px;
      border-radius: 20px;

      .icon-accent {
        color: #7367f0;
      }

      .label {
        font-size: 0.7rem;
        color: #64748b;
        margin-right: 4px;
        font-weight: 500;
      }

      .value {
        font-size: 0.75rem;
        color: #1e293b;
        font-weight: 700;
      }
    }
  }

  .header-bottom-description {
    background: #f8fafc;
    border-top: 1px solid #f1f5f9;

    .description-content {
      display: flex;
      align-items: center;
    }

    .description-text {
      font-size: 0.8125rem;
      color: #475569;
      font-style: italic;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.premium-stat-box {
  display: flex;
  flex-direction: column;
  padding: 8px 16px;
  border-radius: 12px;
  min-width: 95px;
  transition: all 0.3s ease;
  border: 1px solid transparent;

  .stat-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2px;
  }

  .stat-value {
    font-size: 1.1rem;
    font-weight: 800;
  }

  .stat-label {
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: 0.8;
  }

  &.compact-box {
    padding: 6px 14px;
    min-width: 85px;
  }

  &.accepted {
    background: linear-gradient(
      135deg,
      rgba(40, 199, 111, 0.1) 0%,
      rgba(40, 199, 111, 0.05) 100%
    );
    border-color: rgba(40, 199, 111, 0.2);
    color: #28c76f;
    .stat-top svg {
      color: #28c76f;
    }
    &:hover {
      border-color: #28c76f;
      background: rgba(40, 199, 111, 0.15);
    }
  }

  &.pending {
    background: linear-gradient(
      135deg,
      rgba(255, 159, 67, 0.1) 0%,
      rgba(255, 159, 67, 0.05) 100%
    );
    border-color: rgba(255, 159, 67, 0.2);
    color: #ff9f43;
    .stat-top svg {
      color: #ff9f43;
    }
    &:hover {
      border-color: #ff9f43;
      background: rgba(255, 159, 67, 0.15);
    }
  }

  &.rejected {
    background: linear-gradient(
      135deg,
      rgba(234, 84, 85, 0.1) 0%,
      rgba(234, 84, 85, 0.05) 100%
    );
    border-color: rgba(234, 84, 85, 0.2);
    color: #ea5455;
    .stat-top svg {
      color: #ea5455;
    }
    &:hover {
      border-color: #ea5455;
      background: rgba(234, 84, 85, 0.15);
    }
  }
}

.modern-detail-header {
  .title-text {
    letter-spacing: -0.5px;
    line-height: 1.2;
  }
}

.attachment-input-wrapper {
  position: relative;
}

.uploading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid transparent;
}

// Top Scrollbar for PC Users
.table-scrollbar-top {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  height: 12px;
  margin-bottom: 8px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;

  &::-webkit-scrollbar {
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;

    &:hover {
      background: #94a3b8;
    }
  }

  .table-scrollbar-content {
    height: 1px;
    width: 100%;
  }

  @media (max-width: 768px) {
    display: none;
  }
}

.modern-select-field {
  ::v-deep .vs__dropdown-toggle {
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    padding: 2px 0;
    min-height: 44px;
    background: #ffffff;
    display: flex;
    flex-wrap: nowrap !important;
    align-items: center;
  }

  ::v-deep .vs__selected-options {
    display: flex;
    flex-wrap: wrap;
    padding: 2px 8px;
    max-width: calc(100% - 40px);
    overflow: hidden;
  }

  ::v-deep .vs__selected {
    margin: 2px !important;
    background-color: #f3f0ff !important;
    border: 1px solid #e0d8ff !important;
    border-radius: 6px !important;
    color: #7c5dfa !important;
    font-size: 0.8125rem !important;
    padding: 0 8px !important;
  }

  ::v-deep .vs__actions {
    padding-top: 0;
    flex-shrink: 0 !important;
  }

  ::v-deep .vs__search {
    margin: 0;
    padding: 0 8px;
    height: 38px;
  }
}

.table-wrapper {
  overflow-x: auto !important;
  -webkit-overflow-scrolling: touch;
}

.action-btn-header {
  height: 44px;
  width: 44px;
  min-width: 44px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  background-color: #fff !important;
  border: 1px solid #e0d8ff !important;
  border-radius: 8px !important;
  padding: 0 !important;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #f8f7ff !important;
    border-color: #a29bfe !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.2);
  }

  ::v-deep svg {
    color: #7367f0 !important;
    stroke: #7367f0 !important;
  }
}

.consent-details-view {
  .detail-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 1.5rem;
    transition: all 0.2s ease;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }
  }

  .detail-card-header {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    padding: 1rem 1.25rem;
    border-bottom: 2px solid #e2e8f0;
    display: flex;
    align-items: center;

    h5 {
      color: #1e293b;
      font-weight: 700;
      font-size: 1rem;
      margin-bottom: 0;
    }

    .feather-icon {
      color: #7367f0;
    }
  }

  .detail-card-body {
    padding: 1.5rem 1.25rem;
  }

  .detail-item {
    margin-bottom: 1rem;

    .detail-label {
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      color: #64748b;
      letter-spacing: 0.5px;
      margin-bottom: 0.5rem;
      display: block;
    }

    .detail-value {
      font-size: 1rem;
      color: #1e293b;
      font-weight: 500;
      margin-bottom: 0;
    }

    .detail-value-description {
      font-size: 0.95rem;
      color: #475569;
      line-height: 1.6;
      margin-bottom: 0;
      white-space: pre-wrap;
    }
  }

  .signature-preview {
    margin-top: 0.5rem;
    padding: 1rem;
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
    text-align: center;

    .signature-img {
      max-width: 250px;
      max-height: 150px;
      object-fit: contain;
    }
  }

  .attachment-preview-card {
    background: #f8fafc;
    padding: 1rem;
    border-radius: 8px;
    border: 1px solid #e2e8f0;

    .attachment-icon-box {
      width: 50px;
      height: 50px;
      background: rgba(115, 103, 240, 0.1);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    h6 {
      color: #1e293b;
      font-weight: 600;
    }

    .btn-download-attachment {
      border-radius: 6px;
      font-weight: 600;
      padding: 0.5rem 1rem;
      background: #7367f0;
      border: none;
      color: white;

      &:hover {
        background: #6a4de8;
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(124, 93, 250, 0.3);
      }
    }
  }
}

/* ── Consent Responses Modal ── */
.consent-modal-header {
  padding: 1.5rem 1.75rem 1.25rem;
  background: linear-gradient(135deg, #f8f7ff 0%, #ffffff 100%);
  border-bottom: 1.5px solid #ede9fe;
}

.consent-modal-body {
  padding: 0 0 2rem 0;

  ::v-deep .consent-modal-thead th {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border-bottom: 2px solid #e0d8ff;
    font-weight: 700;
    color: #2d1b69;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.6px;
    padding: 0.75rem 1rem;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  ::v-deep .student-response-table td {
    padding: 0.75rem 1rem;
    vertical-align: middle;
    border-top: 1px solid #f1f5f9;
    font-size: 0.875rem;
    color: #475569;
  }

  ::v-deep .student-response-table tbody tr:hover {
    background: #f3f0ff !important;
    td {
      color: #2d1b69;
    }
  }
}

/* Meta chips in modal header */
.cmh-meta-chip {
  display: inline-flex;
  align-items: center;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #475569;
  border-radius: 20px;
  padding: 3px 12px;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;

  svg {
    color: #7367f0;
  }
}

.cmh-desc-chip {
  background: #faf8ff;
  border-color: #e0d8ff;
  color: #5e5873;
  max-width: 380px;
}

/* Stat boxes in modal header */
.cmh-stat {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 700;
  border: 1.5px solid transparent;
  transition: all 0.2s ease;

  .cmh-stat-num {
    font-size: 1.1rem;
    font-weight: 800;
    line-height: 1;
  }

  .cmh-stat-label {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    opacity: 0.85;
  }

  &.accepted {
    background: rgba(40, 199, 111, 0.08);
    border-color: rgba(40, 199, 111, 0.25);
    color: #28c76f;
  }

  &.pending {
    background: rgba(255, 159, 67, 0.08);
    border-color: rgba(255, 159, 67, 0.25);
    color: #ff9f43;
  }

  &.rejected {
    background: rgba(234, 84, 85, 0.08);
    border-color: rgba(234, 84, 85, 0.25);
    color: #ea5455;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.modern-modal {
  .modal-content {
    border-radius: 20px !important;
    border: none !important;
    box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15) !important;
    overflow: hidden;
  }
  .modal-header {
    border-bottom: 1px solid #f1f5f9;
    padding: 1rem 1.5rem;
    position: relative;
    .modal-title {
      font-weight: 700;
      color: #1e293b;
      font-size: 1.1rem;
    }
    .close {
      position: absolute;
      right: 1.25rem;
      top: 1rem;
      padding: 0;
      margin: 0;
      width: 40px;
      height: 40px;
      background: #f1f5f9;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 1;
      color: #64748b;
      transition: all 0.3s ease;
      font-size: 1.5rem;
      border: none;
      outline: none !important;
      z-index: 99;

      &:hover {
        background: #ea5455;
        color: #fff;
        transform: rotate(90deg);
        box-shadow: 0 4px 8px rgba(234, 84, 85, 0.3);
      }

      span {
        display: block;
        line-height: 1;
        margin-top: -2px;
      }
    }
  }
}

.vs__dropdown-menu {
  z-index: 9999 !important;
}

/* Ensure sticky header background in modal */
.consent-modal-thead th {
  background: #ffffff !important;
}
</style>
